import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { Chip, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listSessionDatasets } from 'api/agent';
import { getCalculatedFeaturesAvailabilitySummary } from 'api/features';
import { filter, map } from 'es-toolkit/compat';
import { SessionDataset } from 'interfaces/agentSession';
import React from 'react';

import { getPrimaryJoinCandidate } from './studyReportDatasetsUtils';

export type StudyReportAvailabilityStatus = 'available' | 'missing' | 'loading';

export interface StudyReportAvailabilityItem {
  label: string;
  status: StudyReportAvailabilityStatus;
  detail: string;
}

export const useStudyReportDataAvailability = ({
  sessionId,
  scopedStudyIds = [],
  datasets,
  isDatasetAvailabilityLoading,
}: {
  sessionId?: string;
  scopedStudyIds?: string[];
  datasets?: SessionDataset[];
  isDatasetAvailabilityLoading?: boolean;
}) => {
  const queryClient = useQueryClient();
  const datasetQueryKey = React.useMemo(() => ['sessionDatasets', sessionId], [sessionId]);
  const normalizedScopedStudyIds = React.useMemo(() => [...scopedStudyIds].sort(), [scopedStudyIds]);

  const datasetsQuery = useQuery({
    queryKey: datasetQueryKey,
    queryFn: () => listSessionDatasets(sessionId as string),
    enabled: Boolean(sessionId) && typeof datasets === 'undefined',
    placeholderData: () => queryClient.getQueryData<SessionDataset[]>(datasetQueryKey),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const featureAvailabilityQuery = useQuery({
    queryKey: ['reportFeatureAvailability', normalizedScopedStudyIds],
    queryFn: async () => {
      const response = await getCalculatedFeaturesAvailabilitySummary(normalizedScopedStudyIds);
      const studySummaries = response.studies || [];

      return {
        studiesWithCalculatedFeatures: studySummaries.filter(
          (studySummary) => studySummary.allSlidesHaveCalculatedFeatures
        ).length,
        studiesWithPartialCalculatedFeatures: studySummaries.filter(
          (studySummary) =>
            !studySummary.allSlidesHaveCalculatedFeatures && studySummary.slidesWithCalculatedFeaturesCount > 0
        ).length,
      };
    },
    enabled: normalizedScopedStudyIds.length > 0,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const resolvedDatasets = datasets ?? datasetsQuery.data ?? [];
  const hasScopedStudyData = scopedStudyIds.length > 0;
  const hasDatasets = resolvedDatasets.length > 0;
  const joinedDatasetCount = React.useMemo(
    () => filter(resolvedDatasets, (dataset) => Boolean(getPrimaryJoinCandidate(dataset))).length,
    [resolvedDatasets]
  );
  const resolvedDatasetAvailabilityLoading =
    isDatasetAvailabilityLoading ??
    (datasetsQuery.isLoading || (datasetsQuery.isFetching && typeof datasetsQuery.data === 'undefined'));
  const isFeatureAvailabilityLoading =
    hasScopedStudyData &&
    (featureAvailabilityQuery.isLoading ||
      (featureAvailabilityQuery.isFetching && typeof featureAvailabilityQuery.data === 'undefined'));
  const studiesWithCalculatedFeatures = featureAvailabilityQuery.data?.studiesWithCalculatedFeatures || 0;
  const studiesWithPartialCalculatedFeatures = featureAvailabilityQuery.data?.studiesWithPartialCalculatedFeatures || 0;

  const availabilityItems = React.useMemo<StudyReportAvailabilityItem[]>(
    () => [
      {
        label: 'Calculated features',
        status: !hasScopedStudyData
          ? 'missing'
          : isFeatureAvailabilityLoading
          ? 'loading'
          : studiesWithCalculatedFeatures > 0
          ? 'available'
          : 'missing',
        detail: !hasScopedStudyData
          ? 'No study selected'
          : isFeatureAvailabilityLoading
          ? 'Checking available feature results...'
          : studiesWithCalculatedFeatures > 0
          ? `Available in ${studiesWithCalculatedFeatures} stud${studiesWithCalculatedFeatures === 1 ? 'y' : 'ies'}`
          : studiesWithPartialCalculatedFeatures > 0
          ? 'Available on some slides, but not the full study scope yet'
          : 'No calculated features detected yet',
      },
      {
        label: 'Marker QC',
        status: hasScopedStudyData ? 'available' : 'missing',
        detail: hasScopedStudyData ? 'Queryable from the active study scope' : 'No study selected',
      },
      {
        label: 'Clinical / session data',
        status: resolvedDatasetAvailabilityLoading ? 'loading' : hasDatasets ? 'available' : 'missing',
        detail: resolvedDatasetAvailabilityLoading
          ? 'Checking uploaded datasets...'
          : hasDatasets
          ? `${resolvedDatasets.length} uploaded dataset${resolvedDatasets.length === 1 ? '' : 's'}`
          : 'No uploaded datasets yet',
      },
      {
        label: 'Case-joined rows',
        status: resolvedDatasetAvailabilityLoading ? 'loading' : joinedDatasetCount > 0 ? 'available' : 'missing',
        detail: resolvedDatasetAvailabilityLoading
          ? 'Checking dataset joins...'
          : joinedDatasetCount > 0
          ? `${joinedDatasetCount} dataset${joinedDatasetCount === 1 ? '' : 's'} matched to cases`
          : 'No dataset joins detected yet',
      },
    ],
    [
      hasDatasets,
      hasScopedStudyData,
      isFeatureAvailabilityLoading,
      joinedDatasetCount,
      resolvedDatasetAvailabilityLoading,
      resolvedDatasets.length,
      studiesWithCalculatedFeatures,
      studiesWithPartialCalculatedFeatures,
    ]
  );

  return {
    availabilityItems,
  };
};

export const StudyReportDataAvailabilitySummary: React.FC<{
  availabilityItems: StudyReportAvailabilityItem[];
  title?: string | null;
  titleVariant?: 'caption' | 'overline' | 'body2';
  showDetails?: boolean;
  inline?: boolean;
}> = ({ availabilityItems, title, titleVariant = 'caption', showDetails = true, inline = false }) => {
  const resolvedTitle = typeof title === 'undefined' ? 'Data available to Copilot in this report' : title;
  const chips = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {map(availabilityItems, (item) => (
        <Chip
          key={item.label}
          size="small"
          variant={item.status === 'available' ? 'filled' : 'outlined'}
          color={item.status === 'available' ? 'success' : item.status === 'loading' ? 'info' : 'default'}
          icon={
            item.status === 'available' ? (
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            ) : item.status === 'loading' ? (
              <HourglassEmptyRoundedIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedRoundedIcon fontSize="small" />
            )
          }
          label={showDetails ? `${item.label}: ${item.detail}` : item.label}
        />
      ))}
    </Stack>
  );

  if (inline) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        {resolvedTitle ? (
          <Typography variant={titleVariant} color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {resolvedTitle}
          </Typography>
        ) : null}
        {chips}
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, maxWidth: '100%' }}>
      {resolvedTitle ? (
        <Typography variant={titleVariant} color="text.secondary">
          {resolvedTitle}
        </Typography>
      ) : null}
      {chips}
    </Stack>
  );
};
